'use client';

import type { ReactNode } from 'react';
import { InfoIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Label } from '../../ui/Label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/Tooltip';

export type FormFieldHelperDisplay = 'icon' | 'text';

export interface FormFieldHelperIconProps {
  helper: string;
  className?: string;
  /** Accessible name when helper copy is only shown in the tooltip. */
  ariaLabel?: string;
}

/** Info icon that reveals `helper` copy in a tooltip on hover/focus. */
export function FormFieldHelperIcon({ helper, className, ariaLabel }: FormFieldHelperIconProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex shrink-0 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className,
            )}
            aria-label={ariaLabel ?? helper}>
            <InfoIcon className="size-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {helper}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface FormFieldLabelProps {
  label: string;
  helper?: string;
  /** `icon` — tooltip on the label row; `text` — helper rendered below the field via {@link FormFieldCaption}. */
  helperDisplay?: FormFieldHelperDisplay;
  htmlFor?: string;
  required?: boolean;
  className?: string;
}

/** Label row with optional info icon at the inline end. */
export function FormFieldLabel({
  label,
  helper,
  helperDisplay = 'text',
  htmlFor,
  required,
  className,
}: FormFieldLabelProps) {
  return (
    <div className={cn('flex w-full items-center gap-1.5', className)}>
      <Label htmlFor={htmlFor} className="min-w-0">
        {label}
        {required ? <span className="text-destructive ms-0.5">*</span> : null}
      </Label>
      {helper && helperDisplay === 'icon' ? (
        <FormFieldHelperIcon helper={helper} className="ms-auto" />
      ) : null}
    </div>
  );
}

export interface FormFieldCaptionProps {
  label: string;
  helper?: string;
  helperDisplay?: FormFieldHelperDisplay;
  htmlFor?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Field shell — label (+ optional info icon), control, optional helper text below.
 * Use `helperDisplay="icon"` when helper copy should not consume vertical space.
 */
export function FormFieldCaption({
  label,
  helper,
  helperDisplay = 'text',
  htmlFor,
  required,
  className,
  children,
}: FormFieldCaptionProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <FormFieldLabel
        label={label}
        helper={helper}
        helperDisplay={helperDisplay}
        htmlFor={htmlFor}
        required={required}
      />
      {children}
      {helper && helperDisplay === 'text' ? (
        <p className="text-xs text-muted-foreground">{helper}</p>
      ) : null}
    </div>
  );
}
